import { useRef, useState, useEffect } from 'react';
import { gsap } from 'gsap';
import { EXTERIOR_RESOURCES } from '../../assetsDictionary';
import './TapToPlace.scss';

const FADE_DURATION = 0.4;
const HOLD_DURATION = 3;
const PULSE_DURATION = 0.95; // mitad del ciclo 1.9s

export default function TapToPlace({ placed, onPlace }) {
  const rootRef = useRef(null);
  const tapRef = useRef(null);
  const swipeRef = useRef(null);
  const pinchRef = useRef(null);
  const timelineStartedRef = useRef(false);
  const pulseTweenRef = useRef(null);
  const [timelinePhase, setTimelinePhase] = useState('idle'); // 'idle' | 'playing' | 'done'

  useEffect(() => {
    if (timelinePhase !== 'idle' || !placed || !tapRef.current) return;
    gsap.set(tapRef.current, { opacity: 0 });
    gsap.fromTo(
      tapRef.current,
      { opacity: 0 },
      {
        opacity: 1,
        duration: FADE_DURATION,
        ease: 'power2.inOut',
        onComplete: () => {
          pulseTweenRef.current = gsap.fromTo(
            tapRef.current,
            { opacity: 0.85, scale: 0.98, xPercent: -50, yPercent: -50 },
            {
              opacity: 1,
              scale: 1.02,
              xPercent: -50,
              yPercent: -50,
              duration: PULSE_DURATION,
              yoyo: true,
              repeat: -1,
              ease: 'power2.inOut',
            }
          );
        },
      }
    );
    return () => {
      pulseTweenRef.current?.kill();
      pulseTweenRef.current = null;
    };
  }, [timelinePhase, placed]);

  const handleClick = () => {
    if (timelineStartedRef.current) return;
    timelineStartedRef.current = true;
    pulseTweenRef.current?.kill();
    pulseTweenRef.current = null;
    setTimelinePhase('playing');
    rootRef.current?.classList.add('tap-to-place--timeline-active');
    onPlace?.();

    const tl = gsap.timeline({
      onComplete: () => {
        gsap.set([tapRef.current, swipeRef.current, pinchRef.current], { opacity: 0 });
        setTimelinePhase('done');
      },
    });
    tl.to(tapRef.current, { opacity: 0, duration: FADE_DURATION, ease: 'power2.inOut' })
      .to(swipeRef.current, { opacity: 1, duration: FADE_DURATION, ease: 'power2.inOut' })
      .to(swipeRef.current, { opacity: 0, duration: FADE_DURATION, ease: 'power2.inOut' }, `+=${HOLD_DURATION}`)
      .to(pinchRef.current, { opacity: 1, duration: FADE_DURATION, ease: 'power2.inOut' })
      .to(pinchRef.current, { opacity: 0, duration: FADE_DURATION, ease: 'power2.inOut' }, `+=${HOLD_DURATION}`);
  };

  const isVisible = (placed && timelinePhase === 'idle') || timelinePhase === 'playing';

  return (
    <div
      ref={rootRef}
      onClick={handleClick}
      className={`tap-to-place ${isVisible ? 'tap-to-place--visible' : 'tap-to-place--hidden'}`}
    >
      <div className="tap-to-place__stack">
        <img
          ref={tapRef}
          src={EXTERIOR_RESOURCES.ImgTapToStart.url}
          alt="Tap to Place"
          className="tap-to-place__img tap-to-place__img--tap"
        />
        <img
          ref={swipeRef}
          src={EXTERIOR_RESOURCES.ImgSwipeToRotate.url}
          alt="Swipte to Rotate"
          aria-hidden
          className="tap-to-place__img tap-to-place__img--swipe"
        />
        <img
          ref={pinchRef}
          src={EXTERIOR_RESOURCES.ImgPinchToZoom.url}
          alt="Pinch to Zoom"
          aria-hidden
          className="tap-to-place__img tap-to-place__img--pinch"
        />
      </div>
    </div>
  );
}
